import { Plus, Minus, X } from "@phosphor-icons/react";
import { fmtMoney } from "@/lib/format";

export function modifierGroupHelper(g) {
  if (!g) return "";
  if (g.repeatable) return "Add multiple";
  const min = Number(g.min_select || 0);
  const max = Number(g.max_select || 0);
  if (min === 1 && max === 1) return "Choose 1";
  if (min === 0 && max > 0)   return `Choose up to ${max}`;
  if (min > 0 && max > 0 && min !== max) return `Choose ${min}–${max}`;
  if (min > 0 && max === min) return `Choose ${min}`;
  return "";
}

// Item's groups in its configured (Menu Builder) order; owner-approved defaults (required groups only) start ticked.
// `gate(group, pickedOptionIds)` optionally hides groups (AI kiosk: combo choices only with the combo).
export function openModifiers(item, mods, gate = null) {
  const groups = item.modifier_group_ids.map((gid) => mods.find((g) => g.id === gid)).filter(Boolean);
  const selections = {};
  for (const g of groups) {
    const d = g.required && (g.options || []).find((o) => o.is_default && !o.archived && !o.out_of_stock);
    if (d) selections[g.id] = { [d.id]: 1 };
  }
  return { item, groups, selections, gate };
}

export function shownGroups(modOpen) {
  if (!modOpen.gate) return modOpen.groups;
  const picked = Object.values(modOpen.selections).flatMap((m) => Object.keys(m).filter((k) => Number(m[k]) > 0));
  return modOpen.groups.filter((g) => modOpen.gate(g, picked));
}

// → { error } or { flat: [cart modifier rows] }
export function flattenModifiers(modOpen) {
  const flat = [];
  for (const g of shownGroups(modOpen)) {
    const entries = Object.entries(modOpen.selections[g.id] || {}).filter(([, q]) => Number(q) > 0);
    const totalQty = entries.reduce((s, [, q]) => s + Number(q), 0);
    if (g.required && totalQty < (g.min_select || 1)) return { error: `Select ${g.min_select || 1} from "${g.name}"` };
    for (const [oid, q] of entries) {
      const opt = g.options.find((o) => o.id === oid);
      if (opt) flat.push({
        modifier_group_id: g.id, modifier_option_id: opt.id, group: g.name, name: opt.name,
        price: opt.price, price_delta: opt.price, qty: Number(q) || 1,
        kds_color: opt.kds_color || "default", // snapshot so later colour edits don't re-colour old tickets
      });
    }
  }
  return { flat };
}

export function ModifierModal({ modOpen, setModOpen, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-[rgba(28,25,23,0.5)] backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 fade-up" data-testid="co-mod-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-medium">{modOpen.item.name}</h3>
          <button onClick={() => setModOpen(null)} data-testid="co-mod-close"><X size={18} /></button>
        </div>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          {shownGroups(modOpen).map((g) => <ModGroup key={g.id} g={g} modOpen={modOpen} setModOpen={setModOpen} />)}
        </div>
        <button data-testid="co-mod-confirm" onClick={onConfirm} className="w-full h-11 mt-5 rounded-md bg-[var(--ink-900)] text-[var(--sand-50)] font-medium">Add</button>
      </div>
    </div>
  );
}

// High-visibility group header: uppercase title + Required/Optional badge + min/max helper text.
function ModGroup({ g, modOpen, setModOpen }) {
  const groupSlug = String(g.name || g.id || "").toLowerCase().replace(/\s+/g, "-");
  const helperText = modifierGroupHelper(g);
  return (
    <div>
      <div
        data-testid={`co-mod-group-header-${groupSlug}`}
        className="mb-3 px-3 py-2.5 rounded-lg bg-[var(--sand-100)] border border-[rgba(28,25,23,0.08)]">
        <div className="flex items-center justify-between gap-2">
          <span className="font-heading text-base font-semibold uppercase tracking-wide leading-tight">
            {g.name}
          </span>
          <span
            data-testid={`co-mod-group-badge-${groupSlug}`}
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-bold shrink-0 ${
              g.required
                ? "bg-[var(--terracotta)] text-white"
                : "bg-white border border-[rgba(28,25,23,0.12)] text-[var(--ink-700)]"
            }`}>
            {g.required ? "Required" : "Optional"}
          </span>
        </div>
        {helperText && (
          <div
            data-testid={`co-mod-group-helper-${groupSlug}`}
            className="text-xs text-[var(--ink-500)] mt-0.5">
            {helperText}
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {g.options.map((o) => <ModOption key={o.id} g={g} o={o} modOpen={modOpen} setModOpen={setModOpen} />)}
      </div>
    </div>
  );
}

// Same selection map shape & repeatable logic as POS: { [option_id]: qty }; out-of-stock and per-option caps respected.
function ModOption({ g, o, modOpen, setModOpen }) {
  const selMap = modOpen.selections[g.id] || {};
  const myQty = Number(selMap[o.id] || 0);
  const on = myQty > 0;
  const isOOS = !!o.out_of_stock;
  const optMax = (o.stock_availability && Number.isFinite(o.stock_availability.max_selectable_quantity))
    ? Math.max(0, Number(o.stock_availability.max_selectable_quantity))
    : null;
  const groupTotal = Object.values(selMap).reduce((s, q) => s + Number(q || 0), 0);
  const effMax = (g.max_select && g.max_select > 0) ? g.max_select : 99;
  const atCap = groupTotal >= effMax;
  const isRepeatable = !!g.repeatable;
  const slug = o.name.toLowerCase().replace(/\s+/g, "-");
  const onAddOrToggle = () => {
    if (isOOS) return;
    setModOpen((p) => {
      const cur = { ...(p.selections[g.id] || {}) };
      const have = Number(cur[o.id] || 0);
      const tot = Object.values(cur).reduce((s, q) => s + Number(q || 0), 0);
      if (isRepeatable) {
        if (tot >= effMax) return p;
        if (optMax !== null && have >= optMax) return p;
        cur[o.id] = have + 1;
      } else if (g.max_select === 1) {
        if (have > 0) {
          delete cur[o.id];
        } else {
          for (const k of Object.keys(cur)) delete cur[k];
          cur[o.id] = 1;
        }
      } else if (have > 0) {
        delete cur[o.id];
      } else if (tot < effMax) {
        cur[o.id] = 1;
      } else {
        return p;
      }
      return { ...p, selections: { ...p.selections, [g.id]: cur } };
    });
  };
  // Silent decrement; qty=0 deletes the key so the modifier disappears from cart / docket / KDS entirely.
  const onMinus = () => setModOpen((p) => {
    const cur = { ...(p.selections[g.id] || {}) };
    const have = Number(cur[o.id] || 0);
    if (have <= 1) delete cur[o.id]; else cur[o.id] = have - 1;
    return { ...p, selections: { ...p.selections, [g.id]: cur } };
  });
  if (isRepeatable) {
    return (
      <div data-testid={`co-mod-row-${slug}`}
           data-out-of-stock={isOOS ? "true" : undefined}
           className={`rounded-md border p-2.5 transition-all ${isOOS ? "border-[rgba(28,25,23,0.12)] bg-[var(--sand-100)] opacity-70" : on ? "border-[var(--terracotta)] bg-[var(--terracotta)]/5" : "border-[rgba(28,25,23,0.12)]"}`}>
        <div className="text-sm font-medium leading-tight mb-1.5 flex items-center justify-between gap-1">
          <span className={`truncate ${isOOS ? "text-[var(--ink-500)]" : ""}`}>{o.name}</span>
          {isOOS ? (
            <span data-testid={`co-mod-oos-${slug}`} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[var(--destructive-bg)] text-[var(--destructive)] font-semibold shrink-0">Out of stock</span>
          ) : (
            o.price > 0 && <span className="text-[11px] text-[var(--ink-500)] tabular shrink-0">+{fmtMoney(o.price)}</span>
          )}
        </div>
        <div className="flex items-center justify-between gap-2">
          <button type="button" data-testid={`co-mod-minus-${slug}`} onClick={onMinus} disabled={myQty === 0} aria-label={`Remove one ${o.name}`}
            className="w-11 h-11 rounded-md border border-[rgba(28,25,23,0.18)] inline-flex items-center justify-center text-[var(--ink-900)] disabled:opacity-30 disabled:cursor-not-allowed active:scale-95 hover:bg-[var(--destructive-bg)] hover:border-[var(--destructive)] hover:text-[var(--destructive)]">
            <Minus size={16} weight="bold" />
          </button>
          <span data-testid={`co-mod-qty-badge-${slug}`} className={`flex-1 text-center text-sm font-semibold tabular ${on ? "text-[var(--terracotta)]" : "text-[var(--ink-500)]"}`}>
            {on ? `x${myQty}` : "—"}
          </span>
          <button type="button" data-testid={`co-mod-${slug}`} onClick={onAddOrToggle} disabled={atCap || isOOS} aria-label={`Add one ${o.name}`}
            className="w-11 h-11 rounded-md bg-[var(--ink-900)] text-[var(--sand-50)] inline-flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed active:scale-95">
            <Plus size={16} weight="bold" />
          </button>
        </div>
      </div>
    );
  }
  return (
    <button
      data-testid={`co-mod-${slug}`}
      data-out-of-stock={isOOS ? "true" : undefined}
      onClick={onAddOrToggle}
      className={`w-full px-3 py-2 rounded-md border text-sm text-left ${isOOS ? "border-[rgba(28,25,23,0.12)] bg-[var(--sand-100)] text-[var(--ink-500)]" : on ? "border-[var(--terracotta)] bg-[var(--terracotta)]/5" : "border-[rgba(28,25,23,0.12)]"} ${isOOS || (atCap && !on) ? "opacity-40 cursor-not-allowed" : ""}`}
      disabled={isOOS || (atCap && !on)}
    >
      <div className="font-medium flex items-center justify-between gap-2">
        <span className={isOOS ? "line-through" : ""}>{o.name}</span>
        {isOOS && (
          <span data-testid={`co-mod-oos-${slug}`} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[var(--destructive-bg)] text-[var(--destructive)] font-semibold shrink-0">Out of stock</span>
        )}
      </div>
      {!isOOS && o.price > 0 && <div className="text-[11px] text-[var(--ink-500)] tabular">+{fmtMoney(o.price)}</div>}
    </button>
  );
}
